import React, { useState, useEffect, useRef, useMemo } from 'react'
import EnhancedLeadsTable, { EnhancedLeadsTableRef } from '@/components/LeadsTable/EnhancedLeadsTable'
import LeadDetailsSidebar from '@/components/LeadDetails/LeadDetailsSidebar'
import { Lead } from '@/types/leads/Table'
import { ToastManager } from '@/components/ui/Toast'
import { useUsers } from '@/contexts/UsersContext'
import { apiRequest } from '@/utils/api-request'

interface UserOption {
  value: string;
  label: string;
  email?: string;
  role?: string;
}

const LeadsAdmin: React.FC = () => {
  const { activeUsers } = useUsers();
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false)
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null)
  const [sidebarDefaultTab, setSidebarDefaultTab] = useState<'attachschool' | undefined>(undefined)

  // Ref to access EnhancedLeadsTable methods
  const leadsTableRef = useRef<EnhancedLeadsTableRef>(null)

  // Auto-open lead from notification click (?lead_uuid=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const leadUuid = params.get('lead_uuid');
    if (!leadUuid) return;
    apiRequest(`/leads/${leadUuid}`).then((r: any) => {
      if (r.success && r.data) {
        setSidebarDefaultTab(undefined);
        setSelectedLead(r.data);
        setIsLeadModalOpen(true);
      }
    }).catch(() => {});
  }, []);

  // Transform active users from context to dropdown format
  const activeUsersForDropdown: UserOption[] = useMemo(() => {
    return activeUsers.map((u) => ({
      value: u.uuid,
      label: u.userName,
      email: u.userEmail,
      role: u.role?.role_name
    }));
  }, [activeUsers]);

  const handleLeadClick = (lead: Lead) => {
    setSidebarDefaultTab(undefined) // Reset to default tab
    setSelectedLead(lead)
    setIsLeadModalOpen(true)
  }

  // Handle "Attach School" click from table
  const handleAttachSchool = (lead: Lead) => {
    setSidebarDefaultTab('attachschool')
    setSelectedLead(lead)
    setIsLeadModalOpen(true)
  }

  const closeLeadModal = () => {
    setIsLeadModalOpen(false)
    setSelectedLead(null)
    setSidebarDefaultTab(undefined)
  }

  const handleLeadUpdate = async () => {
    console.log('🔄 Lead updated, refreshing table...')
    await leadsTableRef.current?.refreshLeads()
  }

  return (
    <div className="h-full flex flex-col">
      <ToastManager />
      <div className="flex-1 overflow-auto">
        <EnhancedLeadsTable
          ref={leadsTableRef}
          onLeadClick={handleLeadClick}
          onAttachSchool={handleAttachSchool}
          apiEndpoint="/leads/admin"
          pageTitle="Admin Leads"
          isAdminView={true}
        />
      </div>

      <LeadDetailsSidebar
        isOpen={isLeadModalOpen}
        onClose={closeLeadModal}
        lead={selectedLead}
        activeUsersForDropdown={activeUsersForDropdown}
        onUpdate={handleLeadUpdate}
        isAdminView={true}
        defaultTab={sidebarDefaultTab}
      />
    </div>
  )
}

export default LeadsAdmin
